import React, { useEffect, useMemo, useState } from "react";
import { GeoJSON, MapContainer, Marker, Popup, Polyline, TileLayer } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { formatRelativeTime } from "../helpers/time";
import { normalizeLocation } from "../helpers/normalizers";
import ReactDOMServer from "react-dom/server";
import HomeIcon from "@material-ui/icons/Home";
import WhatshotIcon from "@material-ui/icons/Whatshot";
import MotorcycleIcon from "@material-ui/icons/Motorcycle";
import StoreIcon from "@material-ui/icons/Store";
import AccessibilityNewIcon from "@material-ui/icons/AccessibilityNew";

const DEFAULT_CENTER = [-34.6037, -58.3816]; // Buenos Aires como fallback
const UNASSIGNED_COLOR = "#8e44ad";

const hasLocation = (location) =>
  location &&
  typeof location.lat === "number" &&
  !Number.isNaN(location.lat) &&
  typeof location.long === "number" &&
  !Number.isNaN(location.long);

const renderIcon = (element) => ReactDOMServer.renderToStaticMarkup(element);

export const createMarkerIcon = (color, size, className, iconMarkup) =>
  L.divIcon({
    className: `cadete-marker-icon ${className || ""}`,
    html: `
            <div class="marker-wrapper" style="width:${size}px;height:${size}px;">
                <span class="marker-dot" style="border:2px solid ${color}; background:rgba(255,255,255,0.9)"></span>
                ${iconMarkup ? `<span class="marker-icon" style="color:${color}">${iconMarkup}</span>` : ""}
            </div>
        `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });

const normalizeEstado = (estado = "") => estado.toString().toUpperCase().replace(/\s+/g, "_");

const esPedidoEnCamino = (pedido = {}) => {
  const estado = normalizeEstado(pedido.estado || "");
  return estado === "EN_CAMINO" || estado === "AFUERA";
};

const getPedidoAgeInfo = (pedido = {}) => {
  const created = pedido.created_at || pedido.createdAt;
  if (!created) return { label: "Sin datos", isDelayed: false };

  const createdDate = new Date(created);
  if (Number.isNaN(createdDate.getTime())) return { label: "Sin datos", isDelayed: false };

  const diffMinutes = Math.floor((Date.now() - createdDate.getTime()) / 60000);
  const isDelayed = normalizeEstado(pedido.estado || "") === "RECIBIDO" && diffMinutes > 10;

  return {
    label: formatRelativeTime(createdDate),
    isDelayed,
  };
};

const getPedidoPosition = (pedido) => {
  if (!pedido) return null;

  const goingToDestino = esPedidoEnCamino(pedido);
  const target = goingToDestino ? pedido.destino : pedido.origen;

  return hasLocation(target) ? target : null;
};

const formatCountdown = (dateValue) => {
  if (!dateValue) return "Sin timer";
  const target = new Date(dateValue);
  if (Number.isNaN(target.getTime())) return "Sin timer";
  const diffMs = target.getTime() - Date.now();
  if (diffMs <= 0) return "Listo";
  const diffMinutes = Math.ceil(diffMs / 60000);
  if (diffMinutes < 60) return `Faltan ${diffMinutes} min`;
  const diffHours = Math.ceil(diffMinutes / 60);
  if (diffHours < 24) return `Faltan ${diffHours} hs`;
  const diffDays = Math.ceil(diffHours / 24);
  return `Faltan ${diffDays} día${diffDays !== 1 ? "s" : ""}`;
};

const CadeteMap = ({ city, cadetes, selectedCadeteId, loadingDetail, unassignedOrders = [] }) => {
  const [activeUnassignedRoute, setActiveUnassignedRoute] = useState(null);
  const cadeteIcon = useMemo(
    () => renderIcon(<MotorcycleIcon style={{ fontSize: 20 }} />),
    []
  );
  const cadeteIconSelected = useMemo(
    () => renderIcon(<MotorcycleIcon style={{ fontSize: 22 }} />),
    []
  );
  const unassignedIcon = useMemo(
    () => renderIcon(<AccessibilityNewIcon style={{ fontSize: 18 }} />),
    []
  );
  const center = useMemo(() => {
    if (city?.latitud && city?.longitud) {
      return [city.latitud, city.longitud];
    }
    return DEFAULT_CENTER;
  }, [city]);

  const selectedCadete = useMemo(
    () => cadetes.find((cadete) => cadete.id === selectedCadeteId),
    [cadetes, selectedCadeteId]
  );

  const buildPedidoIconMarkup = (goingToDestino, isDelayed, color) =>
    renderIcon(
      <span className="marker-icon-stack" style={{ color }}>
        {goingToDestino ? (
          <HomeIcon style={{ fontSize: 18 }} />
        ) : (
          <StoreIcon style={{ fontSize: 18 }} />
        )}
        {isDelayed && <WhatshotIcon style={{ fontSize: 14, color: "#c62828", marginLeft: 2 }} />}
      </span>
    );

  const unassignedMarkers = useMemo(() => {
    return (unassignedOrders || [])
      .map((pedido) => {
        const origen = normalizeLocation(pedido.origen || {});
        const destino = normalizeLocation(pedido.destino || {});
        return {
          pedido,
          origen,
          destino,
          ageInfo: getPedidoAgeInfo(pedido),
          routing: pedido.routing,
        };
      })
      .filter(({ origen }) => !!origen);
  }, [unassignedOrders]);

  const pedidosMarkers = useMemo(() => {
    if (!selectedCadete) return [];
    return (selectedCadete.pedidos || [])
      .map((pedido) => ({
        cadete: selectedCadete,
        pedido,
        position: getPedidoPosition(pedido),
        routing: pedido.routing,
        goingToDestino: esPedidoEnCamino(pedido),
        ageInfo: getPedidoAgeInfo(pedido),
      }))
      .filter(({ position }) => !!position);
  }, [selectedCadete]);

  useEffect(() => {
    if (!activeUnassignedRoute) return;
    const stillExists = unassignedMarkers.find(
      (item) => item.pedido.id === activeUnassignedRoute.id
    );
    if (!stillExists) setActiveUnassignedRoute(null);
  }, [unassignedMarkers, activeUnassignedRoute]);

  const cadetesToRender = useMemo(() => {
    if (selectedCadete) return [selectedCadete];
    return cadetes;
  }, [cadetes, selectedCadete]);

  return (
      <MapContainer
        center={center}
        zoom={13}
        scrollWheelZoom
        className="cadete-map"
        key={city?.id || "mapa-cadetes"}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />

        {cadetesToRender.map((cadete) => {
          if (!hasLocation(cadete.location)) return null;
          const isSelected = cadete.id === selectedCadeteId;

        return (
          <Marker
            key={`cadete-${cadete.id}`}
            position={[cadete.location.lat, cadete.location.long]}
            icon={createMarkerIcon(
              cadete.color,
              isSelected ? 36 : 32,
              isSelected ? "cadete selected" : "cadete",
              isSelected ? cadeteIconSelected : cadeteIcon
            )}
          >
              <Popup>
                <div className="popup-content">
                  <strong>
                    {cadete.nombre} {cadete.apellido}
                  </strong>
                  <div>Pedidos asignados: {cadete.pedidos?.length || 0}</div>
                  <div>Último tracking: {formatRelativeTime(cadete.lastTrackingAt)}</div>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {unassignedMarkers.map(({ pedido, origen, destino, ageInfo, routing }) => (
          <Marker
            key={`unassigned-${pedido.id}`}
            position={[origen.lat, origen.long]}
            icon={createMarkerIcon(UNASSIGNED_COLOR, 36, "pedido-unassigned", unassignedIcon)}
            eventHandlers={{
              click: () => {
                if (activeUnassignedRoute?.id === pedido.id) {
                  setActiveUnassignedRoute(null);
                  return;
                }
                setActiveUnassignedRoute(
                  destino
                    ? {
                        id: pedido.id,
                        coords: [
                          [origen.lat, origen.long],
                          [destino.lat, destino.long],
                        ],
                        geojson: routing?.geojson,
                        color: UNASSIGNED_COLOR,
                        destino,
                      }
                    : null
                );
              },
            }}
          >
            <Popup>
              <div className="popup-content">
                <div>
                  <strong>{pedido.direccion || pedido.negocio || "Pedido"}</strong>
                </div>
                <div>Estado: {pedido.estado}</div>
                <div>Creado: {ageInfo.label}</div>
                {pedido.timer && <div>Preparación: {formatCountdown(pedido.timer)}</div>}
              </div>
            </Popup>
          </Marker>
        ))}

        {activeUnassignedRoute &&
          (activeUnassignedRoute.geojson ? (
            <GeoJSON
              key={`unassigned-route-${activeUnassignedRoute.id}`}
              data={activeUnassignedRoute.geojson}
              style={{
                color: activeUnassignedRoute.color,
                weight: 4,
                opacity: 0.85,
              }}
            />
          ) : (
            <Polyline
              positions={activeUnassignedRoute.coords}
              pathOptions={{ color: activeUnassignedRoute.color, weight: 4, opacity: 0.85 }}
            />
          ))}

        {activeUnassignedRoute?.destino && (
          <Marker
            key={`unassigned-dest-${activeUnassignedRoute.id}`}
            position={[activeUnassignedRoute.destino.lat, activeUnassignedRoute.destino.long]}
            icon={createMarkerIcon(
              UNASSIGNED_COLOR,
              30,
              "pedido-unassigned-destino",
              renderIcon(<HomeIcon style={{ fontSize: 18 }} />)
            )}
          />
        )}

        {pedidosMarkers.map(({ cadete, pedido, position, goingToDestino, ageInfo }) => (
          <Marker
            key={`pedido-${cadete.id}-${pedido.id}`}
            position={[position.lat, position.long]}
            icon={createMarkerIcon(
              cadete.color,
              36,
              goingToDestino ? "pedido destino" : "pedido origen",
              buildPedidoIconMarkup(goingToDestino, ageInfo.isDelayed, cadete.color)
            )}
          >
            <Popup>
              <div className="popup-content">
                <div>
                  <strong>
                    {(goingToDestino ? pedido.direccion : pedido.negocio) || "Pedido"}
                  </strong>
                </div>
                <div>Estado: {pedido.estado}</div>
                <div>Creado: {ageInfo.label}</div>
                {ageInfo.isDelayed && (
                  <div className="pedido-delayed">
                    <WhatshotIcon style={{ fontSize: 16, color: "#c62828" }} />
                    Sin confirmar (+10m)
                  </div>
                )}
                <div>
                  Cadete: {cadete.nombre} {cadete.apellido}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {pedidosMarkers.map(({ cadete, pedido }) => {
          if (!pedido?.routing?.geojson) return null;
          return (
            <GeoJSON
              key={`route-${cadete.id}-${pedido.id}`}
              data={pedido.routing.geojson}
              style={{
                color: cadete.color,
                weight: 4,
                opacity: 0.85,
              }}
            />
          );
        })}

        {loadingDetail && selectedCadete && (
          <Popup
            position={[
              selectedCadete.location?.lat || center[0],
              selectedCadete.location?.long || center[1],
            ]}
          >
            <div>Cargando ruta...</div>
          </Popup>
        )}
      </MapContainer>
  );
};

export default CadeteMap;
